import { useEffect, useState } from 'react';
import { parseMarkdown } from '../lib/markdown';

import { EhFooter } from './EhFooter';

interface EHLegalPageProps {
  markdownPath: string;
}

export function EHLegalPage({ markdownPath }: EHLegalPageProps) {
  const [content, setContent] = useState<{ title: string; sections: { title: string; content: string }[] } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadMarkdown() {
      try {
        const response = await fetch(markdownPath);
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.statusText}`);
        }

        const text = await response.text();
        const parsedContent = parseMarkdown(text);
        setContent(parsedContent);
      } catch (error) {
        console.error('Failed to load markdown:', error);
      } finally {
        setIsLoading(false);
      }
    }

    void loadMarkdown();
  }, [markdownPath]);

  if (isLoading) {
    return (
      <div className="flex-auto bg-zinc-900 text-white flex flex-col">
        <div className="flex items-center justify-center py-16 flex-grow">
          <div className="animate-spin size-8 border-4 border-white/20 border-t-white rounded-full" />
        </div>
        <EhFooter />
      </div>
    );
  }

  if (!content) {
    return (
      <div className="flex-auto bg-zinc-900 text-white flex flex-col">
        <div className="text-center py-16 text-zinc-300 text-lg flex-grow">
          Failed to load content. Please try again later.
        </div>
        <EhFooter />
      </div>
    );
  }

  return (
    <div className="flex-auto bg-zinc-900 text-white flex flex-col">
      <div className="flex-grow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-4xl font-bold text-white mb-8">{content.title}</h1>

          {content.sections.map((section, index) => (
            <div key={index} className="mb-10">
              {section.title && <h2 className="text-2xl font-semibold text-zinc-100 mb-5">{section.title}</h2>}
              <div
                className="text-zinc-300 space-y-5 text-base"
                dangerouslySetInnerHTML={{ __html: section.content }}
              />
            </div>
          ))}
        </div>
      </div>
      <EhFooter />
    </div>
  );
}
